import Swiper from 'swiper';
import { Navigation, Pagination, Keyboard, Zoom } from 'swiper/modules';

import '../../styles/components/swiper.scss';
import '../../styles/components/lightbox.scss';

import { lockScroll, releaseScroll } from '../utils/scrollLock';

class Lightbox extends HTMLElement {
  constructor() {
    super();

    this.swiper;
    this.lightbox;
    window.global = {};

    this.modules = [Navigation, Pagination, Keyboard, Zoom];

    this.lightboxMedia = this.querySelectorAll('[data-product-image-swiper] [ligthbox-media]');
    this.initLightbox = (this.lightboxMedia && this.lightboxMedia.length > 0);

    if (this.initLightbox) {
      this.initiateLightbox();
    }
  }

  /**
   * Initiates the lightbox
   */
  initiateLightbox() {
    this.lightboxMedia.forEach(mediaEl => {
      mediaEl.querySelector("[lightbox-toggle]").addEventListener('click', this.buildSwiperContainer.bind(this, mediaEl), false);
    });

    // Close lightbox on click
    document.addEventListener('click', ({ target }) => {
      if (target.closest('.lightbox__close') || target.closest('.lightbox.open') && !target.closest('.lightbox__container')) {
        this.closeLightbox();
      }
    }, false);

    // Close lightbox on escape key press
    document.addEventListener('keydown', ({ key }) => {
      if (key === 'Escape') {
        this.closeLightbox();
      }
    });
  }

  /**
   * Create the lightbox skeleton
   */
  createLightboxSkeleton() {
    // Create skeleton needed for lightbox
    this.lightbox = document.createElement('div');
    const lightboxContainer = document.createElement('div');
    const lightboxClose = document.createElement('div');
    const swiperContainer = document.createElement('div');
    const swiperWrapper = document.createElement('div');
    const swiperBtnNext = document.createElement('div');
    const swiperBtnPrev = document.createElement('div');
    const swiperPagination = document.createElement('div');

    const navSVG = `<svg viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" clip-rule="evenodd" d="M16 5C9.925 5 5 9.925 5 16s4.925 11 11 11 11-4.925 11-11S22.075 5 16 5ZM3 16C3 8.82 8.82 3 16 3s13 5.82 13 13-5.82 13-13 13S3 23.18 3 16Zm13.293-4.707a1 1 0 0 1 1.414 0l4 4a1 1 0 0 1 0 1.414l-4 4a1 1 0 0 1-1.414-1.414L18.586 17H11a1 1 0 1 1 0-2h7.586l-2.293-2.293a1 1 0 0 1 0-1.414Z" fill="currentcolor"/></svg>`;
    const closeSVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 198.861 198.861"><path d="M0 190.116 190.117-.001l8.744 8.744L8.744 198.86z"/><path d="M0 8.744 8.744 0l190.117 190.117-8.744 8.744z"/></svg>`;

    // Add classes
    this.lightbox.classList.add('lightbox');
    lightboxContainer.classList.add('lightbox__container');
    lightboxClose.classList.add('lightbox__close');
    lightboxClose.setAttribute('tabindex', '0');
    lightboxClose.innerHTML = closeSVG;
    swiperContainer.classList.add('swiper');
    swiperWrapper.classList.add('swiper-wrapper');
    swiperBtnNext.classList.add('swiper-button-next', 'swiper-arrow');
    swiperBtnPrev.classList.add('swiper-button-prev', 'swiper-arrow');
    swiperPagination.classList.add('swiper-pagination');

    swiperBtnNext.innerHTML = navSVG;
    swiperBtnPrev.innerHTML = navSVG;

    // Append created divs
    lightboxContainer.appendChild(lightboxClose);
    swiperContainer.appendChild(swiperWrapper);
    swiperContainer.appendChild(swiperBtnNext);
    swiperContainer.appendChild(swiperBtnPrev);
    swiperContainer.appendChild(swiperPagination);
    lightboxContainer.appendChild(swiperContainer);
    this.lightbox.appendChild(lightboxContainer);
    document.body.appendChild(this.lightbox);

    // Set variables to reference the lightbox
    global.lightboxRef = document.querySelector('.lightbox');
    global.swiperWrapperRef = document.querySelector('.lightbox .swiper-wrapper');
  };

  /**
   * Builds the swiper container
   */
  buildSwiperContainer(mediaEl) {
    // first close if exist
    this.closeLightbox();

    // Create the lightbox skeleton
    this.createLightboxSkeleton();

    global.swiperWrapperRef.innerHTML = '';
    this.currentSlideIndex = mediaEl.getAttribute('data-slide-index') ?? mediaEl.dataset.slideIndex;
    this.currentSlideIndex = this.currentSlideIndex ?? 0;

    // Create html for lightbox carousel
    this.lightboxMedia.forEach(med => {
      const media = med.cloneNode(true);
      const slide = document.createElement('div');
      const mediaContainer = document.createElement('div');

      // Add classes
      media.classList.add('swiper-zoom-container');
      slide.classList.add('lightbox__swiper-slide', 'swiper-slide');
      mediaContainer.classList.add('lightbox__media');

      // Append medias to the slides, then slides to swiper wrapper
      mediaContainer.appendChild(media);
      slide.appendChild(mediaContainer);
      global.swiperWrapperRef.appendChild(slide);
    });

    // Swiper config
    this.config = {
      modules: this.modules,
      initialSlide: this.currentSlideIndex,
      slidesPerView: 'auto',
      watchOverflow: true,
      speed: 500,
      zoom: {
        maxRatio: 2,
        minRatio: 1,
        toggle: false,
      },
      navigation: {
        nextEl: '.swiper-button-next.swiper-arrow',
        prevEl: '.swiper-button-prev.swiper-arrow',
      },
      pagination: {
        el: '.swiper-pagination',
        type: 'fraction',
      },
      keyboard: {
        enabled: true,
        onlyInViewport: true,
      },
    }

    // Initiate new Swiper instance
    this.swiper = new Swiper('.lightbox .swiper', this.config);

    // Override Swiper zoom toggle events
    if (this.swiper && this.swiper.zoom.enabled) {
      let swiperSlides = Array.from(this.swiper.slides);
      swiperSlides.forEach(slide => this.setupDragAndClick(slide));
    }

    // Add the class to open the lightbox
    global.lightboxRef.classList.add('open');

    // Add color scheme class to lightbox
    global.lightboxRef.classList.add(this.dataset.colorScheme)

    // Add `no-scroll` class to the body to prevent scrolling
    lockScroll();
  }

  /**
   * Handles the override of swiper zoom in/out toggle events to single click
   * prevents click event from triggering on drag
   */
  setupDragAndClick = (slide) => {
    let isDragging = false, startX, startY;

    // when zooming in
    slide.addEventListener('mousedown', (event) => {
      if (this.swiper.zoom.scale && this.swiper.zoom.scale !== 1) {
        isDragging = false;
        startX = event.clientX;
        startY = event.clientY;
      }
    });

    // when dragging
    slide.addEventListener('mousemove', (event) => {
      if (this.swiper.zoom.scale && this.swiper.zoom.scale !== 1) {
        const deltaX = Math.abs(event.clientX - startX);
        const deltaY = Math.abs(event.clientY - startY);

        if (deltaX > 5 || deltaY > 5) {
          isDragging = true;
        }
      }
    });

    slide.addEventListener('mouseup', (event) => {
      if (this.swiper.zoom.scale && this.swiper.zoom.scale !== 1) {
        if (isDragging) {
          event.preventDefault();
          event.stopPropagation();
        }
      }
    });

    // handles click to zoom in/out when not dragging
    slide.addEventListener('click', () => {
      if (!isDragging) {
        if (this.swiper.zoom.scale && this.swiper.zoom.scale === 1) {
          this.swiper.allowTouchMove = this.swiper.zoom.scale !== 1;
          this.swiper.zoom.in();
        } else {
          this.swiper.zoom.out();
          this.swiper.allowTouchMove = this.swiper.zoom.scale === 1;
        }
      }
    });

    // If zoomed in and navigation button clicked, zoom out and enable allowTouchMove
    this.swiper.navigation.prevEl.addEventListener('click', () => {
      if (this.swiper.zoom.scale && this.swiper.zoom.scale !== 1) {
        this.swiper.zoom.out();
        this.swiper.allowTouchMove = this.swiper.zoom.scale === 1;
      }
    });
    this.swiper.navigation.nextEl.addEventListener('click', () => {
      if (this.swiper.zoom.scale && this.swiper.zoom.scale !== 1) {
        this.swiper.zoom.out();
        this.swiper.allowTouchMove = this.swiper.zoom.scale === 1;
      }
    });
  }

  /**
   * Releases the scroll lock and removes the lightbox from the DOM
   */
  closeLightbox = () => {
    global.lightboxRef?.classList.remove('open');
    releaseScroll(); // Remove `no-scroll` class to the body to re-enable scrolling
    this.swiper?.destroy();
    this.lightbox?.remove();
  }
}

try {
  customElements.define('lightbox-element', Lightbox);
}
catch(e) {}
